import { pubText, pubPath } from "../../i18n/publicTranslation";
import { useId } from 'react';
import { Forward10, Replay10, Subject } from '@mui/icons-material';
import characterSource from '../images/screen_one.png';
import ideaSource from '../images/screen_two.png';
import storySource from '../images/screen_three.png';

type Variant = 'character' | 'idea' | 'story';
const CHARACTER_CONTENT = '/images/how-it-works/leo-character-content.webp';
const STORY_CONTENT = '/images/how-it-works/leo-rival-story-content.webp';
const IDEA_BACKGROUND = '/images/how-it-works/mobile-create-background.jpg';
const INK = '#101219';
const GOLD = '#d7a45d';
const BLUE = '#78a6df';

// Keep the original raster frames intact. Only changed content is layered into
// their original coordinates; text stays vector-sharp at every display size.
export default function GoOriginalArtwork({ variant, label }: { variant: Variant; label: string }) {
  const id = useId().replace(/:/g, '');
  const height = variant === 'character' ? 1179 : variant === 'idea' ? 876 : 1687;
  const photoStarts = [413, 584, 755, 926];

  return (
    <svg
      className={`creation-original-art creation-original-art--${variant}`}
      viewBox={`0 0 1520 ${height}`}
      width="1520"
      height={height}
      role="img"
      aria-label={pubText(label)}
      fontFamily="Roboto, Arial, sans-serif"
    >
      {pubText(variant === 'character' && (
        <>
          <defs>
            <clipPath id={`${id}-photos`}>
              <rect x="441" y="730" width="667" height="172" />
            </clipPath>
            {pubText(photoStarts.map((_, index) => (
              <clipPath key={index} id={`${id}-photo-${index}`}>
                <circle cx={528 + index * 183} cy="816" r="85" />
              </clipPath>
            )))}
          </defs>
          <image href={pubPath(characterSource)} width="1520" height="1179" />
          <rect x="440" y="126" width="190" height="49" fill={INK} />
          <text x="443" y="159" fill={BLUE} fontSize="26" fontWeight="500">{pubText(" Leo ")}</text>
          <rect x="440" y="445" width="276" height="67" fill={INK} />
          <rect
            x="445"
            y="451"
            width="111"
            height="55"
            rx="14"
            fill="#262930"
            stroke="#e2ab63"
            strokeWidth="4"
          />
          <rect x="576" y="451" width="135" height="55" rx="14" fill="#262930" />
          <g fill="#f4f4f5" fontSize="25" fontWeight="600" textAnchor="middle">
            <text x="500" y="487">{pubText(" Male ")}</text>
            <text x="643" y="487">{pubText(" Female ")}</text>
          </g>
          <rect x="440" y="564" width="642" height="146" fill={INK} />
          <text x="444" y="608" fill="#fff" fontSize="29" fontWeight="600">{pubText(" Personalize your character ")}</text>
          <circle cx="835" cy="599" r="12" fill="none" stroke="#797b80" strokeWidth="3" />
          <text x="835" y="607" fill="#797b80" fontSize="21" fontWeight="700" textAnchor="middle">{pubText(" i ")}</text>
          <text x="443" y="659" fill="#a6a6ad" fontSize="26" fontWeight="500">
            <tspan x="443">{pubText("Add photos to create a character")}</tspan>
            <tspan x="443" dy="36">{pubText(" who looks like you. ")}</tspan>
          </text>
          <g clipPath={`url(#${id}-photos)`}>
            {pubText(photoStarts.map((start, index) => (
              <g key={start} clipPath={`url(#${id}-photo-${index})`}>
                <svg
                  x={443 + index * 183}
                  y="731"
                  width="170"
                  height="170"
                  viewBox={`${start} 662 163 163`}
                >
                  <image href={pubPath(CHARACTER_CONTENT)} width="1424" height="1105" />
                </svg>
              </g>
            )))}
          </g>
        </>
      ))}
      {pubText(variant === 'idea' && (
        <>
          <defs>
            <mask id={`${id}-idea-frame`}>
              <rect width="1520" height="876" fill="#fff" />
              <rect x="443" y="29" width="618" height="1000" rx="86" fill="#000" />
            </mask>
            <clipPath id={`${id}-idea-screen`}>
              <rect x="443" y="29" width="618" height="1000" rx="86" />
            </clipPath>
            <clipPath id={`${id}-idea-photo`}>
              <circle cx="611" cy="768" r="26" />
            </clipPath>
            <clipPath id={`${id}-justin`}>
              <rect x="797" y="724" width="191" height="89" rx="24" />
            </clipPath>
            <linearGradient id={`${id}-idea-bottom`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0.9" stopColor="#fff" />
              <stop offset="1" stopColor="#000" />
            </linearGradient>
            <mask id={`${id}-idea-fade`}>
              <rect width="1520" height="876" fill={`url(#${id}-idea-bottom)`} />
            </mask>
          </defs>
          <g mask={`url(#${id}-idea-fade)`}>
            <image
              href={pubPath(characterSource)}
              x="77"
              width="1351"
              height="1048"
              mask={`url(#${id}-idea-frame)`}
            />
            <g clipPath={`url(#${id}-idea-screen)`}>
              <image
                href={pubPath(IDEA_BACKGROUND)}
                x="443"
                y="29"
                width="618"
                height="1030"
                preserveAspectRatio="xMidYMid slice"
              />
              <rect x="443" y="29" width="618" height="1030" fill={INK} opacity="0.15" />
            </g>
          </g>
          <g fill={GOLD} fontSize="96" fontWeight="700" textAnchor="middle" letterSpacing="-2">
            <text x="760" y="402">{pubText(" Tonight, ")}<tspan fill={BLUE}>{pubText("Leo")}</tspan>{pubText(" faces ")}</text>
            <text x="760" y="514">{pubText(" his greatest rival ")}</text>
          </g>
          <rect x="462" y="724" width="88" height="88" rx="25" fill="#262930" />
          <path d="M494 768h24m-12-12v24" stroke="#f4f4f5" strokeWidth="4" strokeLinecap="round" />
          <rect
            x="570"
            y="726"
            width="208"
            height="84"
            rx="24"
            fill="#262930"
            stroke={BLUE}
            strokeWidth="4"
          />
          <text x="649" y="779" fill="#f4f4f5" fontSize="32" fontWeight="500">{pubText(" Leo ")}</text>
          <image href={pubPath(ideaSource)} width="1520" height="876" clipPath={`url(#${id}-justin)`} />
          <g clipPath={`url(#${id}-idea-photo)`}>
            <svg x="585" y="742" width="52" height="52" viewBox="413 662 163 163">
              <image href={pubPath(CHARACTER_CONTENT)} width="1424" height="1105" />
            </svg>
          </g>
        </>
      ))}
      {pubText(variant === 'story' && (
        <>
          <defs>
            <clipPath id={`${id}-story-screen`}>
              <rect x="395" y="220" width="733" height="1374" rx="72" />
            </clipPath>
            <clipPath id={`${id}-story-photo`}>
              <circle cx="401" cy="229" r="189" />
            </clipPath>
            <mask id={`${id}-story-frame`}>
              <rect width="1520" height="1687" fill="#fff" />
              <rect x="395" y="220" width="733" height="1374" rx="72" fill="#000" />
              <circle cx="401" cy="229" r="189" fill="#000" />
            </mask>
            <linearGradient id={`${id}-reading`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" stopColor="#101219" stopOpacity="0" />
              <stop offset="0.4" stopColor="#101219" stopOpacity="0.8" />
              <stop offset="0.72" stopColor="#101219" />
            </linearGradient>
            <linearGradient id={`${id}-bottom`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0.92" stopColor="#fff" />
              <stop offset="1" stopColor="#000" />
            </linearGradient>
            <mask id={`${id}-screen-fade`}>
              <rect x="395" y="220" width="733" height="1374" fill={`url(#${id}-bottom)`} />
            </mask>
          </defs>
          <image href={pubPath(storySource)} width="1520" height="1687" mask={`url(#${id}-story-frame)`} />
          <g clipPath={`url(#${id}-story-screen)`} mask={`url(#${id}-screen-fade)`}>
            <rect x="395" y="220" width="733" height="1374" fill={INK} />
            <svg x="395" y="220" width="733" height="1068" viewBox="309 173 578 842">
              <image href={pubPath(STORY_CONTENT)} width="1190" height="1322" />
            </svg>
            <rect x="395" y="900" width="733" height="694" fill={`url(#${id}-reading)`} />
            <path
              d="m743 1074 18 10 18-10"
              stroke="#ffffffc7"
              strokeWidth="4.5"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <text x="761" y="1150" fill="#fff" fontSize="38" fontWeight="700" textAnchor="middle">
              <tspan x="761">{pubText("Leo’s greatest rival stepped")}</tspan>
              <tspan x="761" dy="49">{pubText(" out of the shadows. ")}</tspan>
            </text>
            <text x="761" y="1264" fill="#f1f1f5" fontSize="28" textAnchor="middle">{pubText(" His gloves flickered blue. ")}</text>
            <text x="461" y="1334" fill="#b6b6bf" fontSize="23" textAnchor="middle">
              0:18
            </text>
            <text x="1062" y="1334" fill="#b6b6bf" fontSize="23" textAnchor="middle">
              1:32
            </text>
            <path d="M519 1326h484" stroke="#ffffff1f" strokeWidth="7" strokeLinecap="round" />
            <path d="M519 1326h290" stroke="#ffffff38" strokeWidth="7" strokeLinecap="round" />
            <path d="M519 1326h95" stroke="#fff" strokeWidth="7" strokeLinecap="round" />
            <circle cx="614" cy="1326" r="15" fill="#fff" />
            <rect x="440" y="1390" width="90" height="68" rx="22" fill="#ffffff1a" />
            <text x="485" y="1434" fill="#fff" fontSize="30" fontWeight="600" textAnchor="middle">
              1×
            </text>
            <g transform="translate(585 1390) scale(2.8333)">
              <Replay10 component="g" style={{ color: '#fff' }} />
            </g>
            <g transform="translate(870 1390) scale(2.8333)">
              <Forward10 component="g" style={{ color: '#fff' }} />
            </g>
            <g transform="translate(1008 1398) scale(2.2083)">
              <Subject component="g" style={{ color: '#fff' }} />
            </g>
            <circle cx="761" cy="1424" r="46" fill="#fff" />
            <g fill="#000">
              <rect x="747" y="1407" width="9" height="35" rx="2" />
              <rect x="767" y="1407" width="9" height="35" rx="2" />
            </g>
            <text x="761" y="282" fill="#ffffffad" fontSize="21" textAnchor="middle">{pubText(" Chapter 1 ")}</text>
            <text x="761" y="317" fill="#fff" fontSize="27" fontWeight="600" textAnchor="middle">{pubText(" His greatest rival ")}</text>
          </g>
          <g clipPath={`url(#${id}-story-photo)`}>
            <svg x="212" y="40" width="378" height="378" viewBox="166 38 288 288">
              <image href={pubPath(STORY_CONTENT)} width="1190" height="1322" />
            </svg>
          </g>
        </>
      ))}
    </svg>
  );
}
